"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { generateDemo, adjustDemo, publishDemo, deleteDemo } from "./actions";
import type { StudioBrand } from "./page";

const FONT = { fontFamily: "var(--font-geist-sans), system-ui, sans-serif" };
const MONO = { fontFamily: "var(--font-geist-mono), monospace" };

function DemoRow({ b }: { b: StudioBrand }) {
  const router = useRouter();
  const [view, setView] = useState<"dm" | "email">("dm");
  const [instr, setInstr] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dm, setDm] = useState(b.dm);
  const [email, setEmail] = useState(b.email);
  const [live, setLive] = useState(b.live);
  useEffect(() => {
    setDm(b.dm);
    setEmail(b.email);
    setLive(b.live);
  }, [b.dm, b.email, b.live]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const link = live || `${origin}/${b.slug}`;
  const text = (view === "email" ? email : dm).replace(/\{LINK\}/g, link);

  async function adjust() {
    if (!instr.trim() || adjusting) return;
    setAdjusting(true);
    setErr(null);
    const res = await adjustDemo(b.slug, instr);
    setAdjusting(false);
    if (res.ok) {
      setInstr("");
      if (res.dm !== undefined) setDm(res.dm);
      if (res.email !== undefined) setEmail(res.email);
      router.refresh();
    } else setErr(res.error || "Fehler");
  }

  async function deploy() {
    setDeploying(true);
    setErr(null);
    const res = await publishDemo(b.slug);
    setDeploying(false);
    if (res.ok && res.url) {
      setLive(res.url);
      router.refresh();
    } else setErr(res.error || "Deploy fehlgeschlagen");
  }

  async function del() {
    if (!window.confirm(`Demo "${b.name}" wirklich löschen? Wird auch von Vercel entfernt.`)) return;
    setDeleting(true);
    setErr(null);
    const res = await deleteDemo(b.slug);
    setDeleting(false);
    if (res.ok) router.refresh();
    else setErr(res.error || "Löschen fehlgeschlagen");
  }

  return (
    <li className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-white/20">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-lg font-medium">
            {b.name}
            {live && (
              <span style={MONO} className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.6rem] text-emerald-300">
                LIVE
              </span>
            )}
          </div>
          <div style={MONO} className="mt-1 truncate text-[0.7rem] text-white/40">
            {b.tagline} · {b.system} ·{" "}
            {live ? (
              <a href={live} target="_blank" rel="noopener noreferrer" className="text-[#9c40ff] hover:underline">
                {live.replace(/^https?:\/\//, "")}
              </a>
            ) : (
              `/${b.slug}`
            )}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <a
            href={`/${b.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg bg-gradient-to-r from-[#ee4f27] to-[#9c40ff] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            Vorschau ↗
          </a>
          <button
            onClick={deploy}
            disabled={deploying}
            className="rounded-lg border border-white/15 px-4 py-2 text-sm text-white/80 transition-colors hover:border-emerald-400/60 hover:text-white disabled:opacity-50"
          >
            {deploying ? "deploye …" : live ? "Re-deploy" : "Live schalten"}
          </button>
          <button
            onClick={del}
            disabled={deleting}
            title="Demo löschen"
            className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/50 transition-colors hover:border-red-400/60 hover:text-red-300 disabled:opacity-50"
          >
            {deleting ? "…" : "✕"}
          </button>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <input
          value={instr}
          onChange={(e) => setInstr(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && adjust()}
          placeholder="Anpassen: z.B. 'System noir-cinematic', '3D Kristall', 'dunkler', 'DM lockerer'…"
          disabled={adjusting}
          className="flex-1 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#9c40ff]/60"
        />
        <button
          onClick={adjust}
          disabled={adjusting}
          className="rounded-lg bg-white/10 px-4 py-2 text-sm text-white transition-colors hover:bg-white/20 disabled:opacity-50"
        >
          {adjusting ? "…" : "Anpassen"}
        </button>
      </div>
      {err && <p className="mt-2 text-xs text-red-300">{err}</p>}

      {(dm || email) && (
        <div className="mt-4">
          <div className="mb-2 flex items-center gap-2">
            {(["dm", "email"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                disabled={v === "email" && !email}
                style={MONO}
                className={`rounded-full px-3 py-1 text-[0.62rem] uppercase tracking-wider transition-colors disabled:opacity-30 ${
                  view === v ? "bg-white text-black" : "bg-white/10 text-white/60 hover:text-white"
                }`}
              >
                {v === "dm" ? "DM" : "E-Mail"}
              </button>
            ))}
            <button
              onClick={() => {
                navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              className="ml-auto rounded-lg border border-white/15 px-3 py-1 text-xs text-white/70 hover:text-white"
            >
              {copied ? "kopiert ✓" : "kopieren"}
            </button>
          </div>
          <pre style={FONT} className="whitespace-pre-wrap rounded-lg bg-black/40 p-4 text-[0.82rem] leading-relaxed text-white/60">
            {text || "(keine E-Mail-Version)"}
          </pre>
        </div>
      )}
    </li>
  );
}

const TIERS: { tier: string; rows: { name: string; ch: string }[] }[] = [
  {
    tier: "Tier 1 · sofort",
    rows: [
      { name: "Zahnärzte · Ästhetik · Schönheits-OP · Privatkliniken", ch: "DM/Mail" },
      { name: "Juweliere · Goldschmiede · Uhrmacher · Manufakturen", ch: "DM" },
      { name: "Gehobene Gastro · Hotels · Weingüter", ch: "DM" },
      { name: "Innenarchitekten · Architekten · Küchenstudios · Premium-Handwerk", ch: "Mail/DM" },
    ],
  },
  {
    tier: "Tier 2",
    rows: [
      { name: "Immobilienmakler · Bauträger", ch: "Mail" },
      { name: "Auto: Oldtimer · Tuning · Premium-Werkstatt", ch: "DM" },
      { name: "Medical Spa · Haartransplantation · Laser", ch: "DM" },
      { name: "Kanzleien: Anwalt · Steuerberater · Notar", ch: "Mail" },
    ],
  },
  {
    tier: "Tier 3",
    rows: [
      { name: "Boutique-Fitness · Yoga", ch: "DM" },
      { name: "Hochzeits- & Eventlocations", ch: "DM" },
      { name: "Premium-Bestatter", ch: "Mail" },
    ],
  },
];

function TargetGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02]">
      <button onClick={() => setOpen((o) => !o)} style={MONO} className="flex w-full items-center justify-between px-5 py-3 text-[0.7rem] tracking-[0.3em] text-white/50">
        ZIELBRANCHEN · ALTE SEITE + KAUFKRAFT
        <span>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="space-y-5 px-5 pb-5">
          {TIERS.map((t) => (
            <div key={t.tier}>
              <div style={MONO} className="mb-2 text-[0.62rem] uppercase tracking-wider text-[#ff8a5c]">
                {t.tier}
              </div>
              <ul className="space-y-1.5">
                {t.rows.map((r) => (
                  <li key={r.name} className="flex items-center justify-between gap-3 text-sm text-white/70">
                    <span>{r.name}</span>
                    <span style={MONO} className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[0.6rem] text-white/50">
                      {r.ch}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="text-xs text-white/30">
            Heißestes Signal: aktiv auf Instagram, aber Website von 2015. DM-Branchen über Insta, Mail-Branchen über die E-Mail-Version.
          </p>
        </div>
      )}
    </div>
  );
}

export function StudioClient({ brands }: { brands: StudioBrand[] }) {
  const router = useRouter();
  const [url, setUrl] = useState("");
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onGenerate() {
    if (!url.trim() || busy) return;
    setBusy(true);
    setError(null);
    setStatus("scrape → art-direct → live … (15-40 s)");
    const res = await generateDemo(url, instructions);
    setBusy(false);
    if (res.ok) {
      setStatus(`✓ /${res.slug} generiert`);
      setUrl("");
      setInstructions("");
      router.refresh();
    } else {
      setStatus(null);
      setError(res.error || "Fehler");
    }
  }

  return (
    <main style={FONT} className="relative min-h-screen overflow-hidden bg-[#080808] text-white">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/3 h-[50vh] w-[50vh] rounded-full bg-[#ee4f27] opacity-20 blur-[160px]" />
        <div className="absolute right-1/4 top-20 h-[45vh] w-[45vh] rounded-full bg-[#9c40ff] opacity-20 blur-[160px]" />
      </div>

      <div className="relative mx-auto max-w-3xl px-6 py-16">
        <header className="mb-12">
          <div style={MONO} className="mb-3 text-[0.6rem] uppercase tracking-[0.3em] text-white/40">
            Bibliothek · 6 Systeme + Motive
          </div>
          <h1 className="text-3xl font-medium tracking-tight">
            Aus der{" "}
            <span className="bg-gradient-to-r from-[#ff6a3d] to-[#9c40ff] bg-clip-text text-transparent">
              Vorlagen-Bibliothek
            </span>
          </h1>
        </header>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onGenerate()}
            placeholder="prospect-seite.de"
            disabled={busy}
            className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white outline-none placeholder:text-white/30 focus:border-[#9c40ff]/60"
          />
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Anpassungen (optional): z.B. 'System kinetic-bold', '3D Kristall', 'dunkler & edler' …"
            rows={2}
            disabled={busy}
            className="mt-3 w-full resize-none rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/30 focus:border-[#9c40ff]/60"
          />
          <button
            onClick={onGenerate}
            disabled={busy}
            className="mt-3 w-full rounded-lg bg-gradient-to-r from-[#ee4f27] to-[#9c40ff] px-6 py-3 font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "läuft …" : "Generate"}
          </button>
          {status && <p className="mt-3 text-sm text-white/50">{status}</p>}
          {error && <p className="mt-3 rounded-lg bg-red-500/10 px-4 py-2 text-sm text-red-300">{error}</p>}
        </div>

        <TargetGuide />

        <h2 style={MONO} className="mt-12 mb-6 text-[0.7rem] tracking-[0.3em] text-white/40">
          DEMOS · {brands.length}
        </h2>

        <ul className="flex flex-col gap-4">
          {brands.map((b) => (
            <DemoRow key={b.slug} b={b} />
          ))}
        </ul>
      </div>
    </main>
  );
}
